import React, { useCallback, useEffect, useRef, useState } from 'react';
import './progress-teardrop.css';
import { Paragraph } from '../../paragraph';
import PropTypes from 'prop-types';
import { Icon } from '../../icon';
import classNames from 'classnames';

const ProgressTeardrop = props => {
    const {
        type,
        titlePosition,
        previousScore,
        updatedScore,
        ariaHideTeardrop,
    } = props;

    const scoreUpdaterInterval = useRef(null);
    const teardropPositiveProgressBlocker = useRef(null);
    const teardropNegativeProgressBlocker = useRef(null);
    const resizeTimeout = useRef(null);

    const [rotationScore, setRotationScore] = useState(previousScore);
    const [rotationStarted, setRotationStarted] = useState(false);

    const maxScoreForHalfRotation = 10;

    const isPositive = rotationScore >= 0;
    const isNegative = rotationScore <= 0;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getRotationValue = useCallback(score => {
        const percentageOfScore = score / maxScoreForHalfRotation;

        return percentageOfScore * 180 > 360
            ? 360
            : percentageOfScore * 180 < -360
            ? -360
            : percentageOfScore * 180;
    });
    const [rotationValue, setRotationValue] = useState(
        getRotationValue(previousScore),
    );

    const pauseAnimations = () => {
        teardropPositiveProgressBlocker.current.classList.add(
            'teardrop__resize-animation-stopper',
        );
        teardropNegativeProgressBlocker.current.classList.add(
            'teardrop__resize-animation-stopper',
        );
        clearTimeout(resizeTimeout.current);
        resizeTimeout.current = setTimeout(() => {
            teardropPositiveProgressBlocker.current.classList.remove(
                'teardrop__resize-animation-stopper',
            );
            teardropNegativeProgressBlocker.current.classList.remove(
                'teardrop__resize-animation-stopper',
            );
        }, 500);
    };

    useEffect(() => {
        window.addEventListener('resize', pauseAnimations);
        return () => {
            window.removeEventListener('resize', pauseAnimations);
            clearTimeout(resizeTimeout.current);
        };
    }, []);

    useEffect(() => {
        setRotationValue(getRotationValue(rotationScore));
    }, [getRotationValue, rotationScore]);

    useEffect(() => {
        const scoreStartTimeout = setTimeout(() => {
            setRotationStarted(true);
            if (previousScore < updatedScore) {
                scoreUpdaterInterval.current = setInterval(() => {
                    setRotationScore(rotationScore => rotationScore + 1);
                }, 300);
            } else if (previousScore > updatedScore) {
                scoreUpdaterInterval.current = setInterval(() => {
                    setRotationScore(rotationScore => rotationScore - 1);
                }, 300);
            }
        }, 300);
        return () => {
            clearTimeout(scoreStartTimeout);
            clearInterval(scoreUpdaterInterval.current);
        };    }, [previousScore, updatedScore]);

    useEffect(() => {
        if (rotationScore === updatedScore) {
            clearInterval(scoreUpdaterInterval.current);
        } else {
            setRotationValue(getRotationValue(rotationScore));
        }
    }, [getRotationValue, rotationScore, updatedScore]);

    const scoreRotationStyles = {
        transform: `rotate(${rotationValue}deg)`,
    };

    const negativeRotationStyles = isNegative ? scoreRotationStyles : null;
    const positiveRotationStyles = isPositive ? scoreRotationStyles : null;
    const isDecreasingPastHalfRotationPositive =
        updatedScore > 0 &&
        previousScore > 0 &&
        updatedScore < previousScore &&
        previousScore > maxScoreForHalfRotation;
    const isDecreasingPastHalfRotationNegative =
        updatedScore < 0 &&
        previousScore < 0 &&
        updatedScore > previousScore &&
        previousScore < -maxScoreForHalfRotation;

    const teardropProgressPositiveClasses = classNames({
        'teardrop__progress--right': true,
        'teardrop__progress--positive': isDecreasingPastHalfRotationNegative
            ? rotationScore > -maxScoreForHalfRotation
            : rotationScore >= -maxScoreForHalfRotation,
        'teardrop__progress--negative': isDecreasingPastHalfRotationNegative
            ? rotationScore <= -maxScoreForHalfRotation
            : rotationScore < -maxScoreForHalfRotation,
    });

    const teardropProgressNegativeClasses = classNames({
        'teardrop__progress--left': true,
        'teardrop__progress--negative': isDecreasingPastHalfRotationPositive
            ? rotationScore < maxScoreForHalfRotation
            : rotationScore <= maxScoreForHalfRotation,
        'teardrop__progress--positive': isDecreasingPastHalfRotationPositive
            ? rotationScore >= maxScoreForHalfRotation
            : rotationScore > maxScoreForHalfRotation,

        'teardrop__progress--flipped': rotationScore > maxScoreForHalfRotation,
    });

    const teardropProgressBlockerRightClasses = classNames({
        'rotate-positive': true,
        'teardrop__progress--blocker': true,
        'teardrop__progress--right': isDecreasingPastHalfRotationNegative
            ? rotationScore > -maxScoreForHalfRotation
            : rotationScore >= -maxScoreForHalfRotation,
        'teardrop__progress--left': isDecreasingPastHalfRotationNegative
            ? rotationScore <= -maxScoreForHalfRotation
            : rotationScore < -maxScoreForHalfRotation,
        'teardrop__progress--blocker-no-transition':
            rotationScore < 0 || !rotationStarted,
        'teardrop__progress--blocker-flipped-to-red': isDecreasingPastHalfRotationNegative
            ? rotationScore <= -maxScoreForHalfRotation
            : rotationScore < -maxScoreForHalfRotation,
    });

    const teardropProgressBlockerLeftClasses = classNames({
        'teardrop__progress--blocker': true,
        'rotate-negative': true,
        'teardrop__progress--left': isDecreasingPastHalfRotationPositive
            ? rotationScore < maxScoreForHalfRotation
            : rotationScore <= maxScoreForHalfRotation,
        'teardrop__progress--right': isDecreasingPastHalfRotationPositive
            ? rotationScore >= maxScoreForHalfRotation
            : rotationScore > maxScoreForHalfRotation,
        'teardrop__progress--blocker-no-transition':
            rotationScore > 0 || !rotationStarted,
        'teardrop__progress--blocker-flipped-to-green': isDecreasingPastHalfRotationPositive
            ? rotationScore >= maxScoreForHalfRotation
            : rotationScore > maxScoreForHalfRotation,
    });

    return (
        <div
            className="teardrop__container"
            tabIndex={ariaHideTeardrop === true ? -1 : 0}
            role="figure"
            aria-label={'score' + updatedScore ?? '0'}
            aria-hidden={ariaHideTeardrop}
        >
            {titlePosition === 'top' && (
                <Paragraph
                    colorScheme="dark"
                    fontWeight="bold"
                    withSpacing={false}
                    textAlign="center"
                >
                    {type}
                </Paragraph>
            )}
            <span className="teardrop">
                <div className="teardrop__progress">
                    <span className={teardropProgressPositiveClasses} />
                    <span className={teardropProgressNegativeClasses} />
                    <span
                        className={teardropProgressBlockerLeftClasses}
                        style={negativeRotationStyles}
                        ref={teardropPositiveProgressBlocker}
                    />
                    <span
                        className={teardropProgressBlockerRightClasses}
                        style={positiveRotationStyles}
                        ref={teardropNegativeProgressBlocker}
                    />
                    <div className="teardrop__icon-background">
                        {rotationScore > 0 ? (
                            <Icon
                                iconName="plus"
                                size="small"
                                colorScheme="green"
                                altText="positive"
                            />
                        ) : rotationScore < 0 ? (
                            <span
                                className="teardrop__minus"
                                role="img"
                                aria-label="negative"
                            />
                        ) : null}
                    </div>
                </div>
            </span>
            {titlePosition === 'bottom' && (
                <Paragraph
                    colorScheme="dark"
                    fontWeight="bold"
                    withSpacing={false}
                    textAlign="center"
                >
                    {type}
                </Paragraph>
            )}
        </div>
    );
};

ProgressTeardrop.defaultProps = {
    titlePosition: 'bottom',
    previousScore: 0,
    updatedScore: 0,
};

ProgressTeardrop.propTypes = {
    type: PropTypes.oneOf([
        'Media Profile',
        'Party Reputation',
        'Voter Support',
    ]).isRequired,
    titlePosition: PropTypes.oneOf(['top', 'bottom']),
    previousScore: PropTypes.number,
    updatedScore: PropTypes.number,
};

export { ProgressTeardrop };
